// Native replacements for the Ember array prototype extensions, removed now
// that `EXTEND_PROTOTYPES.Array` is false.
// https://deprecations.emberjs.com/id/deprecate-array-prototype-extensions
//
// The array is the first argument: `items.sortBy('seq')` -> `sortBy(items, 'seq')`.
// Values are read via `get()`, so dotted paths and Ember objects still resolve.
// Any iterable is accepted (RecordArray, ManyArray, Set); a plain array comes
// back.
//
// Each function iterates the source directly rather than copying it first, so
// the ones returning an array allocate only the result, and the ones returning
// a scalar allocate nothing.
//
// MIGRATION TRAPS (each of these bit us during HERO-8232).
//
// Most breakage throws `x.foo is not a function`, which is easy to find. These
// three do NOT — they silently produce wrong data, so hunt them deliberately:
//
//   1. `firstObject`/`lastObject` inside a KEY PATH. `get(x, 'firstObject.name')`
//      and `sortBy('firstObject.name')` resolve to undefined on a plain array
//      instead of throwing. Two sorts were silently no-ops. Use `x[0]`/`x.at(-1)`,
//      and for a sort key `.sort((a, b) => compare(a[0]?.name, b[0]?.name))`.
//   2. `uniq()` takes NO argument. `uniq('id')` silently ignored it and deduped
//      by identity; `uniqBy('id')` is the property-based one.
//   3. `pushObjects` returned the ARRAY; `push` returns a LENGTH. Anywhere the
//      result is used — `let x = [].pushObjects(y)`, `return acc.pushObjects(y)`
//      in a reduce — spread instead. This one fails later and far away.
//
// The rest throw, but are easy to miss when reading:
//
//   4. EmberArray methods hand back a PLAIN array. `recordArray.filterBy(...)`,
//      `A(x).uniq()` and friends produce something with no `.sortBy`, so a chain
//      breaks at its SECOND link, not its first.
//   5. `reduce(fn, [])` is plain twice over: the accumulator inside the callback
//      AND the result you chain onto, even when every input is an EmberArray.
//      `reduce(fn, A())` keeps both Ember-ish.
//   6. Enum `.values` is `Object.freeze([...])` — always plain.
//   7. On an `A()` array prefer `pushObject`/`removeObject` over `push`: the
//      native calls skip KVO, so `@each` dependents stop invalidating.
import { get } from '@ember/object';
import { compare } from '@ember/utils';

// copy returns a mutable plain-array copy, the one case that has to allocate.
function copy<T>(array: Iterable<T>): T[] {
  return Array.isArray(array) ? (array as T[]).slice() : [...array];
}

// objectAt reads an item by index from either a plain array or a genuine
// EmberArray-backed value that isn't a real JS Array and so has no bracket
// index access (e.g. an ember-data-model-fragments FragmentArray). Mirrors
// Ember's own internal objectAt() dispatch — use this instead of bracket
// indexing whenever the source might still be a non-native EmberArray.
// A non-native EmberArray, such as a FragmentArray.
interface ObjectAtArray<T> {
  objectAt(index: number): T | undefined;
}

export function objectAt<T>(
  array: readonly T[] | ObjectAtArray<T>,
  index: number,
): T | undefined {
  return Array.isArray(array)
    ? (array as readonly T[])[index]
    : (array as ObjectAtArray<T>).objectAt(index);
}

// resolved returns the currently-loaded plain array from an EmberData async
// hasMany relation (a PromiseManyArray) without touching its deprecated
// array-proxy surface (`.reduce()`, `A()`, `{{sort-by}}`, etc. all throw on
// the proxy itself). Returns [] if the relation hasn't resolved yet - that's
// indistinguishable here from "resolved but empty", so code that needs to
// tell those apart should await the relation directly instead of using this.
//
// Safe to call on an already-plain array too (e.g. a `{{filter-by}}`/`.map()`
// result passed down the same argument as the raw relation elsewhere) -
// a plain array has no `.content`, so without this check it would come back
// as `[]` instead of passing through.
export function resolved<T>(
  relation: readonly T[] | { content?: readonly T[] | null } | null | undefined,
): readonly T[] {
  if (Array.isArray(relation)) {
    return relation as readonly T[];
  }
  return (
    (relation as { content?: readonly T[] | null } | null | undefined)
      ?.content ?? []
  );
}

// compact drops null/undefined. Keeps 0, '' and false.
export function compact<T>(array: Iterable<T | null | undefined>): T[] {
  const result: T[] = [];
  for (const item of array) {
    if (item !== null && item !== undefined) {
      result.push(item);
    }
  }
  return result;
}

// byKeys builds an `Array#sort` comparator ordering by each key in turn. Uses
// Ember's type-aware `compare()`, not the string coercion sort defaults to.
// Use it directly when the array is already a throwaway (a `.filter()` or
// `.map()` result), to sort in place instead of allocating another copy:
//   items.filter(...).sort(byKeys('sequence'))
export function byKeys<T>(...keys: string[]): (a: T, b: T) => number {
  // Single key is the common case and skips the inner loop entirely.
  if (keys.length === 1) {
    const key = keys[0] as string;
    return (a, b) => compare(get(a, key), get(b, key));
  }
  return (a, b) => {
    for (const key of keys) {
      const result = compare(get(a, key), get(b, key));
      if (result !== 0) {
        return result;
      }
    }
    return 0;
  };
}

// sortBy sorts a copy by each key in turn, leaving the source untouched.
export function sortBy<T>(array: Iterable<T>, ...keys: string[]): T[] {
  return copy(array).sort(byKeys<T>(...keys));
}

// findBy returns the first match. With no `value`, tests truthiness.
export function findBy<T>(
  array: Iterable<T>,
  key: string,
  value?: unknown,
): T | undefined {
  const truthy = arguments.length === 2;
  for (const item of array) {
    const actual = get(item, key);
    if (truthy ? actual : actual === value) {
      return item;
    }
  }
  return undefined;
}

// filterBy returns all matches. With no `value`, tests truthiness.
export function filterBy<T>(
  array: Iterable<T>,
  key: string,
  value?: unknown,
): T[] {
  const truthy = arguments.length === 2;
  const result: T[] = [];
  for (const item of array) {
    const actual = get(item, key);
    if (truthy ? actual : actual === value) {
      result.push(item);
    }
  }
  return result;
}

// rejectBy is the inverse of filterBy.
export function rejectBy<T>(
  array: Iterable<T>,
  key: string,
  value?: unknown,
): T[] {
  const truthy = arguments.length === 2;
  const result: T[] = [];
  for (const item of array) {
    const actual = get(item, key);
    if (truthy ? !actual : actual !== value) {
      result.push(item);
    }
  }
  return result;
}

// mapBy returns the value at `key` for each item.
export function mapBy<T, K extends keyof T & string>(
  array: Iterable<T>,
  key: K,
): T[K][];
export function mapBy(array: Iterable<unknown>, key: string): unknown[];
export function mapBy(array: Iterable<unknown>, key: string): unknown[] {
  const result: unknown[] = [];
  for (const item of array) {
    result.push(get(item, key));
  }
  return result;
}

// uniq drops duplicates, keeping the first occurrence.
export function uniq<T>(array: Iterable<T>): T[] {
  return [...new Set(array)];
}

// uniqBy drops items whose `key` was already seen.
export function uniqBy<T>(array: Iterable<T>, key: string): T[] {
  const seen = new Set<unknown>();
  const result: T[] = [];
  for (const item of array) {
    const value = get(item, key);
    if (!seen.has(value)) {
      seen.add(value);
      result.push(item);
    }
  }
  return result;
}

// without drops every occurrence of `value`.
export function without<T>(array: Iterable<T>, value: T): T[] {
  const result: T[] = [];
  for (const item of array) {
    if (item !== value) {
      result.push(item);
    }
  }
  return result;
}

// isAny tests whether any item matches. With no `value`, tests truthiness.
export function isAny<T>(
  array: Iterable<T>,
  key: string,
  value?: unknown,
): boolean {
  const truthy = arguments.length === 2;
  for (const item of array) {
    const actual = get(item, key);
    if (truthy ? actual : actual === value) {
      return true;
    }
  }
  return false;
}

// isEvery tests whether every item matches. With no `value`, tests truthiness.
export function isEvery<T>(
  array: Iterable<T>,
  key: string,
  value?: unknown,
): boolean {
  const truthy = arguments.length === 2;
  for (const item of array) {
    const actual = get(item, key);
    if (truthy ? !actual : actual !== value) {
      return false;
    }
  }
  return true;
}
